import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEmailSettings, updateEmailSettings } from "../../api/emailSettings";
import { toast } from "react-toastify";
import { IoEyeOff } from "react-icons/io5";
import { BsEye } from "react-icons/bs";

export default function EmailSettingsAdmin() {
  const qc = useQueryClient();

  // 1) Fetch existing settings
  const { data, isLoading, isError } = useQuery({
    queryKey: ["emailSettings"],
    queryFn: getEmailSettings,
    retry: false,
  });

  // Form state, including raw strings for comma-lists
  const [form, setForm] = useState({
    host: "",
    port: 587,
    secure: false,
    auth: { user: "", pass: "" },
    from: "",
    defaultToRaw: "", // <— raw string
    defaultCcRaw: "", // <— raw string
  });
  const [showPassword, setShowPassword] = useState(false);

  // Populate when data arrives
  useEffect(() => {
    if (!data) return;
    setForm({
      host: data.host || "",
      port: data.port || 587,
      secure: data.secure || false,
      auth: {
        user: data.auth?.user || "",
        pass: data.auth?.pass || "",
      },
      from: data.from || "",
      defaultToRaw: data.defaultTo?.join(", ") || "",
      defaultCcRaw: data.defaultCc?.join(", ") || "",
    });
  }, [data]);

  // Mutation to save
  const mutation = useMutation({
    mutationFn: updateEmailSettings,
    onSuccess: () => {
      toast.success("Email settings saved!");
      qc.invalidateQueries({ queryKey: ["emailSettings"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Save failed");
    },
  });

  // Handle all input changes
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "secure") {
      setForm((f) => ({ ...f, secure: checked }));
      return;
    }

    // raw fields for comma-lists
    if (name === "defaultToRaw" || name === "defaultCcRaw") {
      setForm((f) => ({ ...f, [name]: value }));
      return;
    }

    // nested auth fields?
    if (name.startsWith("auth.")) {
      const key = name.split(".")[1];
      setForm((f) => ({
        ...f,
        auth: { ...f.auth, [key]: value },
      }));
      return;
    }

    // everything else
    setForm((f) => ({
      ...f,
      [name]: type === "number" ? +value : value,
    }));
  };

  // On submit, parse defaultToRaw/defaultCcRaw into arrays
  const onSubmit = (e) => {
    e.preventDefault();

    // helper: split & trim non-empty
    const parseList = (raw) =>
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

    const payload = {
      host: form.host,
      port: form.port,
      secure: form.secure,
      auth: { ...form.auth },
      from: form.from,
      defaultTo: parseList(form.defaultToRaw),
      defaultCc: parseList(form.defaultCcRaw),
    };

    mutation.mutate(payload);
  };

  if (isLoading) return <p>Loading settings…</p>;
  if (isError) return <p className="text-red-600">Failed to load.</p>;

  return (
    <div className="p-6 max-w-xl">
      <h2 className="text-2xl font-bold mb-4">Email Settings</h2>
      <form onSubmit={onSubmit} className="space-y-6">
        {/* SMTP host/port/secure */}
        <div>
          <label className="block mb-1">SMTP Host</label>
          <input
            name="host"
            type="text"
            value={form.host}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div>
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <label className="block mb-1">Port</label>
            <input
              name="port"
              type="number"
              value={form.port}
              onChange={onChange}
              className="text-primary w-full border px-2 py-1 rounded"
            />
          </div>
          {/* <div className="flex items-center">
            <input
              name="secure"
              type="checkbox"
              checked={form.secure}
              onChange={onChange}
              className="text-primary h-4 w-4"
            />
            <label className="ml-2">Secure (SSL/TLS)</label>
          </div> */}
        </div>

        {/* Auth */}
        <div>
          <label className="block mb-1">SMTP User</label>
          <input
            name="auth.user"
            type="email"
            value={form.auth.user}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div>
        {/* <div>
          <label className="block mb-1">SMTP Password</label>
          <input
            name="auth.pass"
            type="text"
            value={form.auth.pass}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div> */}

        <div className="relative">
          <label className="block mb-1">SMTP Password</label>
          <input
            name="auth.pass"
            type={showPassword ? "text" : "password"}
            value={form.auth.pass}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-11 transform -translate-y-1/2 text-gray-500"
          >
            {showPassword ? <IoEyeOff size={18} /> : <BsEye size={18} />}
          </button>
        </div>

        {/* Envelope */}
        <div>
          <label className="block mb-1">From Address</label>
          <input
            name="from"
            type="email"
            value={form.from}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div>

        {/* Envelope: Default To */}
        <div>
          <label className="block mb-1">To (comma-separated)</label>
          <input
            name="defaultToRaw"
            type="text"
            value={form.defaultToRaw}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div>

        {/* Envelope: Default CC */}
        <div>
          <label className="block mb-1">CC (comma-separated)</label>
          <input
            name="defaultCcRaw"
            type="text"
            value={form.defaultCcRaw}
            onChange={onChange}
            className="text-primary w-full border px-2 py-1 rounded"
          />
        </div>

        <button
          type="submit"
          disabled={mutation.isLoading}
          className="bg-accent text-white px-4 py-2 rounded hover:  opacity-90"
        >
          {mutation.isLoading ? "Saving…" : "Save Settings"}
        </button>
      </form>
    </div>
  );
}
